/**
 * Push the platform's enabled languages (from the branding payload) into
 * the runtime :mod:`languageRegistry` so the ``/:lang`` gate, switcher and
 * SEO hreflang reflect admin-added locales.
 *
 * Reads the same cached ``GET /public/branding`` response that
 * ``useShellI18nOverrides`` already consumes (React Query dedupes), so
 * this adds no network cost. Runs on the public shells (tenant + platform
 * marketing); the platform-admin shell warms the registry from its own
 * ``/admin/platform-languages`` fetch instead.
 */

import { useEffect } from "react";

import { useTenantBranding } from "@/api/queries/branding";
import { setActiveLanguages } from "@/i18n/languageRegistry";

export function useRegisterAvailableLanguages(): void {
  // Runs on every shell (``GET /public/branding`` is no-auth and resolves the
  // platform fallback on the admin host), so the ``/:lang`` gate + switcher
  // honour admin-added locales on the platform-admin shell too — not only the
  // public shells. Shares the cached branding query with useShellI18nOverrides.
  const branding = useTenantBranding();
  const languages = branding.data?.available_languages;
  // Track deep changes without an unstable object dep.
  const fingerprint = languages ? JSON.stringify(languages) : "";

  useEffect(() => {
    if (!languages || languages.length === 0) return;
    setActiveLanguages(
      languages.map((l) => ({
        code: l.code,
        label: l.label,
        isDefault: l.is_default,
        source: l.source === "builtin" ? "builtin" : "custom",
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint]);
}
